import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';

import './pages/app-home';
import './components/header';
import './components/site-header.js';
import './styles/global.css';
import { resolveRouterPath } from './lib/router-path';
import { router } from './router';

/** Fragment `#contact` targets `app-home` shadow; browser cannot scroll there without this. */
function scrollHomeTalkToUsIfNeeded(): void {
  const u = router.context.url;
  const homePath = new URL(resolveRouterPath(), window.location.href).pathname;
  if (u.pathname !== homePath || u.hash !== '#contact') return;
  const run = (): void => {
    document.querySelector('app-home')?.shadowRoot?.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  requestAnimationFrame(() => requestAnimationFrame(run));
}

/** SW precache targets built assets only; skip in dev to avoid Workbox noise and bad caching. */
function registerProductionServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const run = () => void navigator.serviceWorker.register('/sw.js');
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

registerProductionServiceWorker();

@customElement('app-index')
export class AppIndex extends LitElement {
  static styles = css`
    :host {
      position: relative;
      display: block;
      min-height: 100%;
    }

    /* Routed pages bring their own .wrap; no extra horizontal inset (matches header/footer). */
    main {
      padding-inline: 0;
      padding-bottom: 1rem;
    }
  `;

  async firstUpdated(changed: PropertyValues): Promise<void> {
    super.firstUpdated(changed);
    router.addEventListener('route-changed', async () => {
      if ('startViewTransition' in document) {
        const vt = (document as any).startViewTransition(() => this.requestUpdate());
        await vt.finished;
      } else {
        this.requestUpdate();
        await this.updateComplete;
      }
      scrollHomeTalkToUsIfNeeded();
    });
    await this.updateComplete;
    scrollHomeTalkToUsIfNeeded();
  }

  render() {
    // router config can be round in src/router.ts
    return html`
      <site-header></site-header>
      ${router.render()}
    `;
  }
}
